import React, { createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { AccountIcon, ListIcon, MailIcon, MedalIcon, SparklesIcon } from "../util/Icons";
import RaceReadyHero from "./RaceReady/RaceReadyHero";

export const HEALTH_AND_FITNESS_BUNDLE_LINKS = [
  {
    label: "MEAL Prepping 101",
    href: "https://drive.google.com/file/d/1GhoLMEpNuf48A_6805e7OGIdg2XavhG2/view?usp=sharing",
  },
  {
    label: "Fit in 15 Minutes - Fit, Strong, and Healthy Busy Women",
    href: "https://drive.google.com/file/d/1EYXinWBidkUHDHZ1p9bTYtRss6m6EER-/view?usp=sharing",
  },
  {
    label: "Self-Care Monthly Planner",
    href: "https://drive.google.com/file/d/1N8J3LKGTs2atpWHZSW7iWBJzc1YVI8Mi/view?usp=sharing",
  },
  {
    label: "30-Day Squat Challenge",
    href: "https://drive.google.com/file/d/1YZu-rPv6Bi_CvcRMFB5wq6CXvuDL6D3t/view?usp=sharing",
  },
  {
    label: "30-Day Plank Challenge",
    href: "https://drive.google.com/file/d/1uFLNUjJjR-YRETGV-PKJJpagocnr4scn/view?usp=sharing",
  },
];

// Holds the "Get race-ready" checklist and the facts for its page header. When it's there, the layout swaps the
// banner + serif title for the race-ready header (race logo instead of the banner), shows the checklist under it, and the older next-step cards
// it replaces (device, dashboard, referral, BIB links) hide themselves.
export const RaceReadyContext = createContext({ checklist: null, hero: null });

function useHasChecklist() {
  return Boolean(useContext(RaceReadyContext).checklist);
}

function LinkOrAnchor({ to, href, children, className = "", target, rel }) {
  if (to) {
    return (
      <Link to={to} className={className} target={target} rel={rel}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={className} target={target || "_blank"} rel={rel || "noreferrer"}>
      {children}
    </a>
  );
}

function getCardTone(tone = "default") {
  switch (tone) {
    case "soft":
      return "border-[#dae4f4] bg-[#f5f8ff]";
    case "highlight":
      return "border-[#f0d8c7] bg-[#fff8f2]";
    default:
      return "border-[#ead7c9] bg-white";
  }
}

export function getDisplayRaceName(raceData, selectedRace) {
  return raceData?.name || selectedRace?.raceInfo?.name || "your challenge";
}

export function getConfirmationTitle(firstName) {
  return firstName ? `${firstName} You Are In!` : "You Are In!";
}

export function getRegistrationSubtitle(raceName, selectedRace) {
  const legName = selectedRace?.raceInfo?.legName?.trim?.() || "";
  const rawDistance = selectedRace?.raceInfo?.distance;
  const distance = rawDistance !== undefined && rawDistance !== null && `${rawDistance}`.trim() !== "" ? `${rawDistance}`.trim() : "";
  const legIncludesDistance = legName && distance && legName.toLowerCase().includes(distance.toLowerCase());
  const details = [];

  if (legName) {
    details.push(legName);
  }
  if (distance && !legIncludesDistance) {
    details.push(`${distance} km`);
  }

  return details.length > 0
    ? `Thank you for registering for ${raceName} - ${details.join(" | ")}`
    : `Thank you for registering for ${raceName}`;
}

export function getDashboardId(selectedRace) {
  return selectedRace?.legId?.replace(/^price_/, "") || "";
}

export function getBoughtProducts(selectedRace) {
  if (!selectedRace?.boughtProducts || typeof selectedRace.boughtProducts !== "object") return [];
  return Object.values(selectedRace.boughtProducts);
}

export function hasProduct(selectedRace, productName) {
  return getBoughtProducts(selectedRace).includes(productName);
}

export function hasProductMatching(selectedRace, pattern) {
  if (!pattern) return false;
  return pattern.test(getBoughtProducts(selectedRace).join(" "));
}

function renderIntroParagraph(paragraph, index) {
  if (typeof paragraph !== "string") {
    return (
      <p key={`confirmation_intro_${index}`} className="m-0">
        {paragraph}
      </p>
    );
  }

  const sentences = paragraph
    .match(/[^.!?]+[.!?]+|[^.!?]+$/g)
    || [paragraph];
  const normalizedSentences = sentences
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  if (normalizedSentences.length < 2) {
    return (
      <p key={`confirmation_intro_${index}`} className="m-0">
        {paragraph}
      </p>
    );
  }

  return (
    <div key={`confirmation_intro_${index}`} className="space-y-3">
      {normalizedSentences.map((sentence, sentenceIndex) => (
        <p key={`confirmation_intro_${index}_${sentenceIndex}`} className="m-0">
          {sentence}
        </p>
      ))}
    </div>
  );
}

export function ConfirmationLayout({
  bannerSrc,
  bannerAlt,
  title,
  subtitle,
  introParagraphs = [],
  helpHeading = "Need Assistance?",
  helpText = "If you need help or have any questions, please email us at",
  helpEmail,
  closingText,
  signOff = "Happy running,",
  signature,
  children,
}) {
  const { checklist, hero } = useContext(RaceReadyContext);
  return (
    <div className="mx-auto w-full max-w-4xl pb-3 text-slate-700">
      {checklist && (
        <div className="mb-6 flex flex-col gap-6 md:mb-8 md:gap-8">
          <RaceReadyHero {...hero}>
            {introParagraphs.length > 0 && introParagraphs.map((paragraph, index) => renderIntroParagraph(paragraph, index))}
          </RaceReadyHero>
          {checklist}
        </div>
      )}

      {!checklist && bannerSrc && (
        <picture>
          <img
            src={bannerSrc}
            alt={bannerAlt || "Challenge banner"}
            className="mx-auto mb-6 w-full max-w-lg rounded-2xl shadow-[0_12px_30px_rgba(15,23,42,0.12)] md:max-w-xl"
          />
        </picture>
      )}

      <div className="mx-auto flex max-w-3xl flex-col gap-6 md:gap-8">
        {!checklist && <div className="text-center">
          <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-800 md:text-5xl">{title}</h1>
          {subtitle && <p className="mt-3 text-sm font-medium text-[#c56d33] md:mt-4 md:text-base">{subtitle}</p>}
          {introParagraphs.length > 0 && (
            <div className="mx-auto mt-3 max-w-2xl space-y-3 text-left text-[15px] leading-7 text-slate-600 md:mt-4 md:text-base">
              {introParagraphs.map((paragraph, index) => renderIntroParagraph(paragraph, index))}
            </div>
          )}
        </div>}

        {children}

        <ConfirmationCard icon={<MailIcon size={20} color="#c56d33" />} title={helpHeading} center className="mt-2">
          <p className="m-0">{helpText}</p>
          {helpEmail && (
            <p className="m-0">
              <a href={`mailto:${helpEmail}`} className="font-semibold text-[#c56d33] hover:underline">
                {helpEmail}
              </a>
            </p>
          )}
          {(closingText || signature) && <div className="my-2 h-px w-full bg-[#ead7c9]" />}
          {closingText && <p className="m-0 italic text-[#c56d33]">{closingText}</p>}
          {signature && (
            <div className="space-y-1">
              <p className="m-0 font-semibold text-slate-800">{signOff}</p>
              <p className="m-0">{signature}</p>
            </div>
          )}
        </ConfirmationCard>
      </div>
    </div>
  );
}

export function ConfirmationSectionTitle({ children }) {
  const hasChecklist = useHasChecklist();
  if (hasChecklist && children === "Your Next Steps") children = "Your extras";
  return (
    <div className="space-y-3">
      <h2 className="m-0 font-serif text-2xl font-semibold text-slate-800 md:text-3xl">{children}</h2>
      <div className="h-px w-full bg-[#ead7c9]" />
    </div>
  );
}

export function ConfirmationCard({ icon, title, children, actions = [], tone = "default", center = false, className = "" }) {
  return (
    <div
      className={`rounded-[22px] border p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] md:p-6 ${getCardTone(tone)} ${className}`}
    >
      <div className={center ? "space-y-4 text-center" : "flex flex-col gap-4 md:flex-row"}>
        {icon && (
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#edf4ff] text-[#c56d33] ${center ? "mx-auto" : ""
              }`}
          >
            {icon}
          </div>
        )}

        <div className={`${center ? "" : "min-w-0 flex-1"}`}>
          {title && <h3 className="m-0 font-serif text-xl font-semibold text-slate-800">{title}</h3>}
          <div className={`mt-3 space-y-3 text-[14px] leading-7 text-slate-600 md:text-[15px] ${center ? "mx-auto max-w-2xl" : ""}`}>
            {children}
          </div>
        </div>
      </div>

      {actions.length > 0 && (
        <div className={`mt-5 flex flex-wrap gap-x-5 gap-y-3 border-t border-[#ead7c9] pt-4 ${center ? "justify-center" : ""}`}>
          {actions.map((action, index) => (
            <ConfirmationActionLink key={`confirmation_action_${index}`} {...action} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ConfirmationActionLink({ to, href, label, button = false, target, rel }) {
  const baseClassName = button
    ? "inline-flex items-center justify-center rounded-full bg-[#ff851f] px-8 py-3 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(255,133,31,0.35)] transition hover:-translate-y-0.5 hover:bg-[#f2760d]"
    : "inline-flex items-center gap-2 text-sm font-semibold text-[#c56d33] transition hover:underline";

  return (
    <LinkOrAnchor to={to} href={href} className={baseClassName} target={target} rel={rel}>
      {!button && <span aria-hidden="true">•</span>}
      <span>{label}</span>
    </LinkOrAnchor>
  );
}

export function ConfirmationBulletList({ items = [] }) {
  return (
    <ul className="m-0 list-disc space-y-3 pl-5 marker:text-[#c56d33]">
      {items.map((item, index) => (
        <li key={`confirmation_bullet_${index}`}>{item}</li>
      ))}
    </ul>
  );
}

export function DeviceTrackingCard({ title = "Connect Your Device", description, note, actions = [] }) {
  if (useHasChecklist()) return null;
  return (
    <ConfirmationCard icon={<AccountIcon size={20} color="#27364b" />} title={title} actions={actions}>
      <p className="m-0">{description}</p>
      {note && <p className="m-0 italic text-slate-500">{note}</p>}
    </ConfirmationCard>
  );
}

export function BibCommunityCard({ title = "BIB, Community and Medal", items = [], actions = [] }) {
  // The checklist covers the BIB, so this card is left out when the checklist is on the page.
  if (useHasChecklist()) return null;
  return (
    <ConfirmationCard icon={<SparklesIcon size={20} color="#c56d33" />} title={title} actions={actions}>
      <ConfirmationBulletList items={items} />
    </ConfirmationCard>
  );
}

export function DashboardCtaCard({ description, to, label = "Go to Dashboard" }) {
  if (useHasChecklist()) return null;
  return (
    <ConfirmationCard tone="soft" center className="py-6">
      <p className="m-0">{description}</p>
      <div className="pt-1">
        <ConfirmationActionLink to={to} label={label} button />
      </div>
    </ConfirmationCard>
  );
}

export function ResourceLinksCard({ title, intro, links = [], icon = <ListIcon size={20} color="#c56d33" /> }) {
  return (
    <ConfirmationCard icon={icon} title={title}>
      {intro && <p className="m-0">{intro}</p>}
      <ConfirmationBulletList
        items={links.map((link) => (
          <LinkOrAnchor to={link.to} href={link.href} className="font-medium text-[#c56d33] hover:underline">
            {link.label}
          </LinkOrAnchor>
        ))}
      />
    </ConfirmationCard>
  );
}

export function ReferralCard({ description, details, to = "/referralhub", label = "Visit Referral Hub" }) {
  if (useHasChecklist()) return null;
  return (
    <ConfirmationCard icon={<ListIcon size={20} color="#c56d33" />} title="Invite Your Friends and Earn Cool Rewards">
      {description && <p className="m-0">{description}</p>}
      {details && <p className="m-0">{details}</p>}
      <div className="pt-1">
        <ConfirmationActionLink to={to} label={label} />
      </div>
    </ConfirmationCard>
  );
}

export function ProgramAccessBlock({ title, description, password, href, linkLabel }) {
  return (
    <ConfirmationCard icon={<MedalIcon size={20} color="#c56d33" />} title={title} tone="highlight">
      <p className="m-0">
        {description}{" "}
        {password && (
          <>
            Use the password <strong>{password}</strong>.
          </>
        )}
      </p>
      <p className="m-0">
        <a href={href} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
          {linkLabel || href}
        </a>
      </p>
    </ConfirmationCard>
  );
}

export function GiftProductsCard({
  selectedRace,
  giftDescription,
  giftGuideHref,
  giftGuideLabel = "See gift code instructions",
  merchandiseText,
  children,
}) {
  const boughtProducts = getBoughtProducts(selectedRace);
  const giftCodes = Object.entries(selectedRace?.giftCodes || {});
  const extraContent = React.Children.toArray(children).filter(Boolean);
  const hasGiftCodes = giftCodes.length > 0;
  const showMerchandise = merchandiseText && hasProductMatching(selectedRace, /socks/i);
  const hasExtraContent = extraContent.length > 0;
  const showPurchasedProductsList = boughtProducts.length > 0 && !showMerchandise && !hasExtraContent;

  if (!hasGiftCodes && !showMerchandise && !hasExtraContent && !showPurchasedProductsList) {
    return null;
  }

  return (
    <>
      {hasGiftCodes && (
        <ConfirmationCard icon={<ListIcon size={20} color="#c56d33" />} title="Gift Registrations">
          {giftDescription && <p className="m-0">{giftDescription}</p>}
          {giftGuideHref && (
            <p className="m-0">
              <a href={giftGuideHref} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                {giftGuideLabel}
              </a>
            </p>
          )}
          <p className="m-0 font-semibold text-slate-800">Your gift codes</p>
          <ul className="mt-3 space-y-2">
            {giftCodes.map(([code, value]) => (
              <li key={code} className="break-all text-sm leading-6 text-slate-600">
                <span className="font-semibold text-slate-800">{code}</span> Valid for: {value}
              </li>
            ))}
          </ul>
        </ConfirmationCard>
      )}

      {showPurchasedProductsList && (
        <ConfirmationCard icon={<ListIcon size={20} color="#c56d33" />} title="Purchased Add-ons">
          <ul className="mt-3 space-y-2">
            {boughtProducts.map((product, index) => (
              <li key={`${product}_${index}`} className="text-sm leading-6 text-slate-600">
                {product}
              </li>
            ))}
          </ul>
        </ConfirmationCard>
      )}

      {showMerchandise && (
        <ConfirmationCard icon={<ListIcon size={20} color="#c56d33" />} title="Products">
          <p className="m-0 font-medium text-slate-700">{merchandiseText}</p>
        </ConfirmationCard>
      )}

      {extraContent}
    </>
  );
}

export function HighlightCard({ title, paragraphs = [], icon = <MedalIcon size={20} color="#c56d33" />, tone = "highlight", actions = [] }) {
  return (
    <ConfirmationCard icon={icon} title={title} tone={tone} actions={actions}>
      {paragraphs.map((paragraph, index) => (
        <p key={`highlight_paragraph_${index}`} className="m-0">
          {paragraph}
        </p>
      ))}
    </ConfirmationCard>
  );
}

export function StrengthProgramBlock({ title = "Strength Training Program" }) {
  return (
    <ProgramAccessBlock
      title={title}
      description="To access your Strength Training Program workouts, click the link below."
      password="60NR"
      href="https://60northrun.ca/4weeks/?fbclid=IwAR24OXyhxFw2wJfS833a_MQDZgTv-hXZLyITZ_9KV8pyOGVRJhEhHxBoW_M"
    />
  );
}

export function BodyWeightProgramBlock() {
  return (
    <ProgramAccessBlock
      title="101 Body-Weight Exercises"
      description="To access your 101 Body-Weight Exercises workouts, click the link below."
      password="CCC23"
      href="https://bannister.coach/101-exercises-deliverable1638934j/"
    />
  );
}
